import { extractCssValue, parseNumber, updateCssRule, updateSvgStrokeInCss } from './cssUtils';

export const DEFAULT_STYLE_TOKENS = {
    primaryColor: '#2563eb',
    backgroundColor: '#ffffff',
    textColor: '#1f2937',
    borderRadius: '8px',
    fontFamily: 'Inter',
    inputPadding: '12px 16px',
    buttonPadding: '12px 24px',
    groupSpacing: '24px',
    shadow: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)'
};

const COLOR_NAMES = {
    black: '#111827',
    blue: '#2563eb',
    green: '#16a34a',
    grey: '#6b7280',
    gray: '#6b7280',
    indigo: '#4f46e5',
    navy: '#001e62',
    orange: '#ea580c',
    pink: '#db2777',
    purple: '#7c3aed',
    red: '#dc2626',
    teal: '#0d9488',
    white: '#ffffff',
    yellow: '#ca8a04'
};

const FONT_ALIASES = {
    arial: 'Arial',
    georgia: 'Georgia',
    helvetica: 'Helvetica',
    inter: 'Inter',
    montserrat: 'Montserrat',
    playfair: 'Playfair Display',
    'playfair display': 'Playfair Display',
    roboto: 'Roboto',
    system: 'system-ui',
    'system ui': 'system-ui',
    'system-ui': 'system-ui'
};

const SHADOW_PRESETS = {
    none: 'none',
    subtle: '0 1px 2px rgba(0,0,0,0.06)',
    soft: '0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)',
    medium: '0 10px 15px -3px rgba(0,0,0,0.12), 0 4px 6px -4px rgba(0,0,0,0.08)',
    strong: '0 18px 28px -8px rgba(0,0,0,0.22), 0 8px 12px -8px rgba(0,0,0,0.12)'
};

const px = (value) => `${Math.max(0, Math.round(value))}px`;

const extractInputCssValue = (css, property) =>
    extractCssValue(css, '.cfg-input-control', property) ||
    extractCssValue(css, '.cfg-select-input', property);

const normalizeColor = (input) => {
    if (!input) return null;
    if (/(transparent|clear|no background|remove background)/i.test(input)) return 'transparent';

    const hex = input.match(/#(?:[0-9a-f]{3}|[0-9a-f]{6})\b/i)?.[0];
    if (hex) return hex;

    const lower = input.toLowerCase();
    return Object.entries(COLOR_NAMES).find(([name]) => lower.includes(name))?.[1] || null;
};

export const extractStyleTokensFromCss = (css) => {
    const font = extractCssValue(css, '.cfg-header', 'font-family')
        .replace(/['"]/g, '')
        .split(',')[0]
        .trim();

    return {
        primaryColor: extractCssValue(css, '.cfg-next-btn', 'background-color') || DEFAULT_STYLE_TOKENS.primaryColor,
        backgroundColor: extractInputCssValue(css, 'background-color') || DEFAULT_STYLE_TOKENS.backgroundColor,
        textColor: extractCssValue(css, '.cfg-header', 'color') || DEFAULT_STYLE_TOKENS.textColor,
        borderRadius: extractInputCssValue(css, 'border-radius') || DEFAULT_STYLE_TOKENS.borderRadius,
        fontFamily: font || DEFAULT_STYLE_TOKENS.fontFamily,
        inputPadding: extractInputCssValue(css, 'padding') || DEFAULT_STYLE_TOKENS.inputPadding,
        buttonPadding: extractCssValue(css, '.cfg-next-btn', 'padding') || DEFAULT_STYLE_TOKENS.buttonPadding,
        groupSpacing: extractCssValue(css, '.cfg-feature-group', 'margin-bottom') || DEFAULT_STYLE_TOKENS.groupSpacing,
        shadow: extractCssValue(css, '.cfg-overview-card', 'box-shadow') || DEFAULT_STYLE_TOKENS.shadow,
        pageBackground: extractCssValue(css, '.cfg-canvas', 'background-color') || ''
    };
};

export const applyStyleTweaksToCss = (css, tweaks) => {
    let nextCss = css || '';

    if (tweaks.primaryColor) {
        nextCss = updateCssRule(nextCss, '.cfg-next-btn', 'background-color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-back-btn', 'color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-back-btn', 'border-color', `${tweaks.primaryColor}40`);
        nextCss = updateCssRule(nextCss, '.cfg-input-control:focus', 'border-color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-input-control:focus', 'box-shadow', `0 0 0 3px ${tweaks.primaryColor}25`);
        nextCss = updateCssRule(nextCss, '.cfg-input-control:focus-within', 'border-color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-input-control:focus-within', 'box-shadow', `0 0 0 3px ${tweaks.primaryColor}25`);
        nextCss = updateCssRule(nextCss, '.cfg-input-control', 'border', `1px solid ${tweaks.primaryColor}40`);
        nextCss = updateCssRule(nextCss, '.cfg-input-control input[type="radio"]', 'accent-color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-input-control input[type="checkbox"]', 'accent-color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input:focus', 'border-color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input:focus', 'box-shadow', `0 0 0 3px ${tweaks.primaryColor}25`);
        nextCss = updateCssRule(nextCss, '.cfg-select-input', 'border', `1px solid ${tweaks.primaryColor}40`);
        nextCss = updateCssRule(nextCss, '.cfg-select-input input[type="radio"]', 'accent-color', tweaks.primaryColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input input[type="checkbox"]', 'accent-color', tweaks.primaryColor);
        nextCss = updateSvgStrokeInCss(nextCss, tweaks.primaryColor);
    }

    if (tweaks.backgroundColor) {
        nextCss = updateCssRule(nextCss, '.cfg-input-control', 'background-color', tweaks.backgroundColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input', 'background-color', tweaks.backgroundColor);
        nextCss = updateCssRule(nextCss, '.cfg-overview-card', 'background-color', tweaks.backgroundColor);
    }

    if (tweaks.pageBackground) {
        nextCss = updateCssRule(nextCss, '.cfg-canvas', 'background-color', tweaks.pageBackground);
    }

    if (tweaks.textColor) {
        nextCss = updateCssRule(nextCss, '.cfg-header', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-description', 'color', `${tweaks.textColor}bf`);
        nextCss = updateCssRule(nextCss, '.cfg-feature-label', 'color', `${tweaks.textColor}a6`);
        nextCss = updateCssRule(nextCss, '.cfg-input-control', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-input-control label', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-input-control span', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-input-control option', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input label', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input span', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-select-input option', 'color', tweaks.textColor);
        nextCss = updateCssRule(nextCss, '.cfg-overview-card', 'color', tweaks.textColor);
    }

    if (tweaks.borderRadius) {
        nextCss = updateCssRule(nextCss, '.cfg-input-control', 'border-radius', tweaks.borderRadius);
        nextCss = updateCssRule(nextCss, '.cfg-select-input', 'border-radius', tweaks.borderRadius);
        nextCss = updateCssRule(nextCss, '.cfg-next-btn', 'border-radius', tweaks.borderRadius);
        nextCss = updateCssRule(nextCss, '.cfg-back-btn', 'border-radius', tweaks.borderRadius);
        nextCss = updateCssRule(nextCss, '.cfg-overview-card', 'border-radius', tweaks.borderRadius);
    }

    if (tweaks.fontFamily) {
        const fontVal = `'${tweaks.fontFamily}', sans-serif`;
        nextCss = updateCssRule(nextCss, '.cfg-header', 'font-family', fontVal);
        nextCss = updateCssRule(nextCss, '.cfg-description', 'font-family', fontVal);
        nextCss = updateCssRule(nextCss, '.cfg-feature-label', 'font-family', fontVal);
        nextCss = updateCssRule(nextCss, '.cfg-input-control', 'font-family', fontVal);
        nextCss = updateCssRule(nextCss, '.cfg-select-input', 'font-family', fontVal);
    }

    if (tweaks.inputPadding) {
        nextCss = updateCssRule(nextCss, '.cfg-input-control', 'padding', tweaks.inputPadding);
        nextCss = updateCssRule(nextCss, '.cfg-select-input', 'padding', tweaks.inputPadding);
    }

    if (tweaks.buttonPadding) {
        nextCss = updateCssRule(nextCss, '.cfg-next-btn', 'padding', tweaks.buttonPadding);
        nextCss = updateCssRule(nextCss, '.cfg-back-btn', 'padding', tweaks.buttonPadding);
    }

    if (tweaks.groupSpacing) {
        nextCss = updateCssRule(nextCss, '.cfg-feature-group', 'margin-bottom', tweaks.groupSpacing);
    }

    if (tweaks.shadow) {
        nextCss = updateCssRule(nextCss, '.cfg-overview-card', 'box-shadow', tweaks.shadow);
        nextCss = updateCssRule(nextCss, '.cfg-next-btn', 'box-shadow', tweaks.shadow === 'none' ? 'none' : tweaks.shadow);
        nextCss = updateCssRule(nextCss, '.cfg-input-control', 'box-shadow', tweaks.shadow === 'none' ? 'none' : tweaks.shadow);
        nextCss = updateCssRule(nextCss, '.cfg-select-input', 'box-shadow', tweaks.shadow === 'none' ? 'none' : tweaks.shadow);
    }

    return nextCss;
};

export const inferStyleTweaksFromPrompt = (prompt, currentTokens = DEFAULT_STYLE_TOKENS) => {
    const lower = prompt.toLowerCase();
    const tweaks = {};

    const requestedColor = normalizeColor(prompt);
    if (requestedColor) {
        if (/(page background|canvas background|iframe background)/i.test(prompt)) {
            tweaks.pageBackground = requestedColor;
        } else if (requestedColor === 'transparent' || /(background|card|input|field|surface)/i.test(prompt)) {
            tweaks.backgroundColor = requestedColor;
        } else if (/(text|font|label|header|copy)/i.test(prompt)) {
            tweaks.textColor = requestedColor;
        } else {
            tweaks.primaryColor = requestedColor;
        }
    }

    const radiusMatch = lower.match(/(?:radius|round(?:ed|er)?|corners?|edges?)[^\d]*(\d{1,2})\s*px?/i);
    if (radiusMatch) {
        tweaks.borderRadius = px(Number(radiusMatch[1]));
    } else if (/(rounder|more rounded|softer|pill|rounded)/i.test(prompt)) {
        tweaks.borderRadius = px(Math.max(parseNumber(currentTokens.borderRadius) + 6, 14));
    } else if (/(sharper|less rounded|square|squared|less round)/i.test(prompt)) {
        tweaks.borderRadius = px(Math.max(parseNumber(currentTokens.borderRadius) - 6, 0));
    }

    const spacingMatch = lower.match(/(?:spacing|gap|space)[^\d]*(\d{1,2})\s*px?/i);
    if (spacingMatch) {
        tweaks.groupSpacing = px(Number(spacingMatch[1]));
    } else if (/(more spacing|more space|roomier|spread out)/i.test(prompt)) {
        tweaks.groupSpacing = px(parseNumber(currentTokens.groupSpacing) + 8);
    } else if (/(less spacing|less space|tighter|compact)/i.test(prompt)) {
        tweaks.groupSpacing = px(Math.max(parseNumber(currentTokens.groupSpacing) - 8, 8));
    }

    const paddingMatch = lower.match(/(?:padding|pad)[^\d]*(\d{1,2})\s*px?/i);
    if (paddingMatch) {
        const value = Number(paddingMatch[1]);
        const paddingValue = `${px(value)} ${px(Math.round(value * 1.6))}`;
        if (/(button|buttons|next|back|cta)/i.test(prompt)) {
            tweaks.buttonPadding = paddingValue;
        } else {
            tweaks.inputPadding = paddingValue;
        }
    } else if (/(bigger buttons|larger buttons|roomier buttons)/i.test(prompt)) {
        const value = parseNumber(currentTokens.buttonPadding) + 4;
        tweaks.buttonPadding = `${px(value)} ${px(value * 2)}`;
    } else if (/(smaller buttons|compact buttons)/i.test(prompt)) {
        const value = Math.max(parseNumber(currentTokens.buttonPadding) - 4, 6);
        tweaks.buttonPadding = `${px(value)} ${px(value * 2)}`;
    }

    if (/(no shadow|remove shadow|flat|no elevation)/i.test(prompt)) {
        tweaks.shadow = SHADOW_PRESETS.none;
    } else if (/(strong shadow|more shadow|deeper shadow|more elevation)/i.test(prompt)) {
        tweaks.shadow = SHADOW_PRESETS.strong;
    } else if (/(medium shadow|shadow)/i.test(prompt)) {
        tweaks.shadow = SHADOW_PRESETS.medium;
    } else if (/(subtle shadow|light shadow|less shadow|softer shadow)/i.test(prompt)) {
        tweaks.shadow = SHADOW_PRESETS.subtle;
    }

    const requestedFont = Object.entries(FONT_ALIASES).find(([name]) => lower.includes(name))?.[1];
    if (requestedFont) {
        tweaks.fontFamily = requestedFont;
    }

    return tweaks;
};

export const isRevertStylePrompt = (prompt) => /\b(undo|revert|go back|roll back|nvm|never mind)\b/i.test(prompt);

export const describeStyleTweaks = (tweaks) => {
    const labels = {
        primaryColor: 'accent color',
        backgroundColor: 'input/card background',
        pageBackground: 'page background',
        textColor: 'text color',
        borderRadius: 'edge rounding',
        fontFamily: 'font',
        inputPadding: 'input padding',
        buttonPadding: 'button padding',
        groupSpacing: 'feature spacing',
        shadow: 'shadow'
    };

    return Object.entries(tweaks)
        .map(([key, value]) => `${labels[key]} to ${value}`)
        .join(', ');
};
